/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

/** 
  All of the routes for the Material Dashboard 2 PRO React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  11. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 PRO React layouts
import Icon from '@mui/material/Icon'

import profilePicture from 'assets/images/account-icons/default-white.png'
import MDAvatar from 'components/MDAvatar'
import Settings from 'layouts/account/settings'
import ResetPassword from 'layouts/authentication/reset-password'
import SignIn from 'layouts/authentication/sign-in'
import CallsOverview from 'layouts/call-data'
import CallInsights from 'layouts/call-insights'
import InboundAnalytics from 'layouts/dashboards/analytics'
import ManageExclusionForm from 'layouts/exclusion-list/manage-exclusion'
import ExclusionList from 'layouts/exclusion-list/table'
import PhoneNumbers from 'layouts/phone-numbers'
import PayphoneLocations from 'layouts/payphone-locations'
import ManageAmbiguousLocations from 'layouts/special-locations/manage-ambiguous-locations'
import ManageSpecialLocationsForm from 'layouts/special-locations/manage-special-locations'
import SpecialLocations from 'layouts/special-locations/table'
import { ROUTE_PATHS } from 'routes/routePaths'
import { RouteType } from 'routes/routeTypes'

const routes: RouteType[] = [
	{
		type: 'collapse',
		name: '${email}',
		key: 'user-account-sidebar',
		icon: <MDAvatar src={profilePicture} alt="Brooklyn Alice" size="xs" />,
		route: ROUTE_PATHS.ACCOUNT.SETTINGS,
		component: <Settings />,
		authRequired: true,
		noCollapse: true,
	},
	{ type: 'divider', key: 'divider-dashboards' },
	{
		type: 'collapse',
		name: 'Dashboards',
		key: 'dashboards',
		icon: <Icon fontSize="medium">dashboard</Icon>,
		collapse: [
			{
				name: 'Inbound Analytics',
				key: 'analytics',
				route: ROUTE_PATHS.DASHBOARD.ANALYTICS,
				component: <InboundAnalytics />,
				authRequired: true,
			},
		],
	},
	{
		type: 'invisible',
		name: 'Sign In',
		key: 'sign-in',
		route: ROUTE_PATHS.AUTHENTICATION.SIGN_IN,
		component: <SignIn />,
		authRequired: false,
	},
	{
		type: 'invisible',
		name: 'Reset Password',
		key: 'reset-password',
		route: ROUTE_PATHS.AUTHENTICATION.RESET_PASSWORD,
		component: <ResetPassword />,
		authRequired: false,
	},
	{
		type: 'collapse',
		name: 'Call Data',
		key: 'call-data',
		icon: <Icon fontSize="medium">phone</Icon>,
		route: ROUTE_PATHS.CALLS.BASE,
		component: <CallsOverview />,
		authRequired: true,
		noCollapse: true,
	},
	{
		type: 'invisible',
		name: 'Call Insights',
		key: 'call-insights',
		route: ROUTE_PATHS.CALLS.DETAIL,
		component: <CallInsights />,
		authRequired: true,
	},
	{
		type: 'collapse',
		name: 'Exclusion List',
		key: 'exclusion-list',
		icon: <Icon fontSize="medium">phone_disabled</Icon>,
		route: ROUTE_PATHS.EXCLUSION_LIST.BASE,
		component: <ExclusionList />,
		authRequired: true,
		noCollapse: true,
	},
	{
		type: 'invisible',
		name: 'New Exclusions',
		key: 'new-exclusion',
		route: ROUTE_PATHS.EXCLUSION_LIST.NEW,
		component: <ManageExclusionForm />,
		authRequired: true,
		noCollapse: true,
	},
	{
		type: 'invisible',
		name: 'Manage Exclusions',
		key: 'manage-exclusion',
		route: ROUTE_PATHS.EXCLUSION_LIST.DETAIL,
		component: <ManageExclusionForm />,
		authRequired: true,
		noCollapse: true,
	},
	{
		type: 'collapse',
		name: 'Locations',
		key: 'special-locations',
		icon: <Icon fontSize="medium">location_on</Icon>,
		collapse: [
			{
				name: 'Special Locations',
				key: 'special-locations',
				route: ROUTE_PATHS.SPECIAL_LOCATIONS.BASE,
				component: <SpecialLocations />,
				authRequired: true,
			},
			{
				name: 'Ambiguous Locations',
				key: 'ambiguous',
				route: ROUTE_PATHS.SPECIAL_LOCATIONS.AMBIGUOUS,
				component: <ManageAmbiguousLocations />,
				authRequired: true,
			},
			{
				name: 'Payphone Locations',
				key: 'payphone',
				route: ROUTE_PATHS.SPECIAL_LOCATIONS.PAYPHONE,
				component: <PayphoneLocations />,
				authRequired: true,
			},
		],
	},
	{
		type: 'invisible',
		name: 'New Special Locations',
		key: 'new-special-locations',
		route: ROUTE_PATHS.SPECIAL_LOCATIONS.NEW,
		component: <ManageSpecialLocationsForm />,
		authRequired: true,
		noCollapse: true,
	},
	{
		type: 'invisible',
		name: 'Special Locations Details',
		key: 'special-locations-details',
		route: ROUTE_PATHS.SPECIAL_LOCATIONS.DETAIL,
		component: <ManageSpecialLocationsForm />,
		authRequired: true,
		noCollapse: true,
	},
	{
		name: 'Phone Numbers',
		type: 'collapse',
		noCollapse: true,
		key: 'phone-numbers',
		icon: <Icon fontSize="medium">tag</Icon>,
		route: ROUTE_PATHS.PHONE_NUMBERS.BASE,
		component: <PhoneNumbers />,
	},
]

export default routes
